import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import { zoneKey } from '../../../core/field/zones';
import {
  CARD_ASPECT, add, canvasOf, card, fade, fill, place, playerColor, zoneBox,
} from '../fx/common';
import { metalText, putIn } from '../fx/duelistPlate';

/**
 * **デッキの上をめくる**（Issue #609 v2。指示書 `../direction/confirm_decktop.md`。キービジュアルは無い＝方向は人が決めた）。
 * 代表は強欲で謙虚な壺（3 枚めくって 1 枚を手札へ）。めくったカードが**選択肢として並ぶ**ことを見せる。
 *
 * ```
 * 0        めくった人の側（P1 = 左 / P2 = 右）から鋼のパネルが加速して滑り込む（後ろに硬い線）。中に空の枠（暗い窪み）が枚数ぶん。
 *          上に見出し「DECK TOP」
 * 220      急停止（反動・小さな揺れ）
 * 200 + i×250  i 枚目の裏のカード（めくった人のスリーブ）がデッキ（盤に写っていれば山の上・無ければ HUD の手札の下）から
 *          弧を描いて飛ぶ（300ms）。収まる直前に横に潰れて、収まった瞬間に表で開く（硬い白の縁 1 フレーム・小さな揺れ）
 * 250 + n×250  全部の枠が埋まる → 見得
 * +500     パネルごと縦に細く潰れて消える（200ms）。帯の終わりに何も残らない（盤に印も置かない）
 * ```
 *
 * どれが選ばれるかは未来なので見ない。選ばれた 1 枚が手札へ行く動きは付けない（move の仕事。ユーザー 2026-10-03）。
 * 盤・HUD は触らない。もやもやした光は使わない（閃光は硬い縁の 1 フレームだけ）。
 */
const IN_END = 220;        // パネルの急停止
const FILL0 = 200;         // 1 枚目が飛び始める
export const GAP = 250;    // 次の 1 枚まで
export const FLY = 300;    // 1 枚が飛ぶ時間
const FLIP = 70;           // 収まる前後に横に潰れて開く時間（片側）
const HOLD = 500;          // 全部が埋まってからの見得
const SQUASH = 200;        // 縦に潰れて消える
const TAIL = 60;           // 帯の終わりの余白（何も残らない）
const TRAIL_MS = 60;

const CARD_H = 330;        // 枠の高さ（4 枚まで）
const SLOT_GAP = 44;
const PAD_X = 84;
const PAD_Y = 58;
const SKEW = 70;
const MAX_W = 1560;        // パネルの幅の上限（多いときは枠を小さくして 1 行に収める）
const EDGE = 5;            // 枠の縁

/** めくった枚数（少なくとも 1） */
export const countOf = (payload) => Math.max(1, payload?.cards?.length ?? 0);
/** i 枚目が飛び始める・収まる時刻 */
export const departOf = (i) => FILL0 + i * GAP;
export const landOf = (i) => departOf(i) + FLY;
/** 全部が埋まる時刻 */
export const filledOf = (n) => landOf(Math.max(1, n) - 1);
/** 消え始める・消え終わる時刻 */
export const squashOf = (n) => filledOf(n) + HOLD;
/** 帯の尺 */
export const durationOf = (n) => squashOf(n) + SQUASH + TAIL;

/**
 * **パネルの寸法**（枠の大きさ・パネルの幅と高さ）。5 枚以上でも幅 `MAX_W` に収まるよう枠を小さくする
 */
export function layoutOf(n) {
  const k = Math.max(1, n);
  const full = k * CARD_H * CARD_ASPECT + (k - 1) * SLOT_GAP + PAD_X * 2 + SKEW;
  const s = Math.min(1, MAX_W / full);
  const ch = CARD_H * s;
  const cw = ch * CARD_ASPECT;
  const gap = SLOT_GAP * s;
  const w = k * cw + (k - 1) * gap + PAD_X * 2 * s + SKEW;
  const h = ch + PAD_Y * 2;
  /** i 枚目の枠の中心（パネルの中心から） */
  const slotX = (i) => (i - (k - 1) / 2) * (cw + gap);
  return { cw, ch, gap, w, h, slotX };
}

const onScreen = (x, y, { w, h }) => Number.isFinite(x) && Number.isFinite(y) && x >= 0 && x <= w && y >= 0 && y <= h;

/**
 * **カードが飛び出す場所**（舞台のピクセル）: 盤にめくった人のデッキが写っていれば山の上、無ければ HUD の手札の下、
 * それも無ければ画面の上の外（その人の側）。返すのは `{ x, y, w }`（w はそこでのカードの幅）
 */
export function deckSpot(ctx, player) {
  const canvas = canvasOf(ctx);
  const box = zoneBox(ctx, zoneKey('maindeck', player));
  if (box && onScreen(box.cx, box.cy, canvas)) {
    return { x: box.cx, y: box.cy, w: Math.max(40, Math.min(box.w, box.h * CARD_ASPECT * 1.1)) };
  }
  const hand = ctx.stage?.hud?.hand?.(player) ?? null;
  if (hand && Number.isFinite(hand.x) && Number.isFinite(hand.y)) {
    return { x: hand.x, y: hand.y + (hand.h ?? 0) / 2 + 40, w: 60 };
  }
  return { x: player === 1 ? canvas.w * 0.8 : canvas.w * 0.2, y: -120, w: 120 };
}

/** i 枚目の飛んだ割合（0 = デッキ・1 = 枠） */
export const flightAt = (t, i) => tween(t, departOf(i), landOf(i), 'power2_out');

export default defineEffect({
  situation: 'confirm_decktop',
  role: 'effect',
  label: 'デッキの上をめくる（選択肢のパネルに 1 枚ずつ補充）',
  color: '#c9a44c',
  // めくったカードが無ければ帯を作らない
  opens: ({ payload }) => ((payload?.cards?.length ?? 0) > 0 ? payload : null),
  duration: ({ payload }) => durationOf(countOf(payload)),
  scene: ({ payload }) => {
    const n = countOf(payload);
    return [
      'reset',
      { ms: IN_END, shake: { amp: 10, freq: 26, decayMs: 180 } },
      ...Array.from({ length: n }, (_, i) => ({ ms: landOf(i), shake: { amp: 4, freq: 30, decayMs: 120 } })),
    ];
  },
  sounds: ({ payload }) => [
    { at: 0, name: 'draw', gain: 0.4 },
    { at: IN_END - 10, name: 'set_card', gain: 0.4 },
    ...Array.from({ length: countOf(payload) }, (_, i) => ({ at: landOf(i) - 10, name: 'set_card', gain: 0.5 })),
  ],
  layers: [{
    id: 'panel',
    label: '空の枠のパネル（滑り込み → 1 枚ずつ補充して表へ → 見得 → 縦に潰れて消える）',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const root = new THREE.Group();
      const { payload } = ctx;
      const cards = payload?.cards ?? [];
      if (!cards.length) return { root };
      const player = payload.player === 1 ? 1 : 0;
      const n = cards.length;
      const { w: W, h: H } = canvasOf(ctx);
      const color = playerColor(ctx, player);
      const L = layoutOf(n);
      const side = player === 1 ? 1 : -1;            // 来る側（-1 = 左 / +1 = 右）
      const cx = W / 2;
      const cy = H / 2 + 50;
      const offDist = W / 2 + L.w / 2 + 60;

      // --- パネル（group。中は中心が原点）
      const g = add(root, new THREE.Group(), 10);
      putIn(g, kit.quad(kit.plain(kit.skewPlateTexture(Math.round(L.w), Math.round(L.h), {
        skew: SKEW, stripe: player === 1 ? 'right' : 'left', color, window: 22,
      }), 1), L.w, L.h), 10);
      // 空の枠（暗い窪み: 黒の縁 → 窪み → 金の細い線 4 辺）
      Array.from({ length: n }).forEach((_, i) => {
        const x = L.slotX(i);
        const rim = fill(ctx, '#050608', L.cw + EDGE * 2 + 6, L.ch + EDGE * 2 + 6);
        rim.position.set(x, 0, 0);
        putIn(g, rim, 10.1);
        const well = fill(ctx, '#15171d', L.cw + EDGE * 2, L.ch + EDGE * 2);
        well.position.set(x, 0, 0);
        putIn(g, well, 10.11);
        [0, 1, 2, 3].forEach((e) => {
          const m = fill(ctx, '#c9a44c', 1, 1);
          const horiz = e % 2 === 0;
          const hw = L.cw / 2 + EDGE; const hh = L.ch / 2 + EDGE;
          place(m, horiz ? x : x + (e === 1 ? hw : -hw), horiz ? (e === 0 ? -hh : hh) : 0, { sx: horiz ? hw * 2 : 2, sy: horiz ? 2 : hh * 2 });
          putIn(g, m, 10.12);
        });
      });
      // 見出し「DECK TOP」（パネルの上の辺に掛けた札）
      const title = metalText(ctx, 'DECK TOP', 72, L.w * 0.6, true);
      const tag = fill(ctx, '#0b0c10', title.w + 40, 70);
      tag.position.set(0, -L.h / 2 - 6, 0);
      putIn(g, tag, 10.2);
      const tagLine = fill(ctx, color, title.w + 40, 4);
      tagLine.position.set(0, -L.h / 2 + 29, 0);
      putIn(g, tagLine, 10.21);
      title.m.position.set(0, -L.h / 2 - 8, 0);
      title.m.scale.set(title.s, title.s, 1);
      putIn(g, title.m, 10.22);

      // --- 硬い線（滑り込みの後ろ。3 本）
      const trails = [-0.3, 0.05, 0.32].map((k, i) => ({ m: add(root, fill(ctx, i === 1 ? '#ffd21a' : '#ffffff', 1, 1), 9), k }));

      // --- 飛んでくるカード（裏 → 収まって表へ）
      const from = deckSpot(ctx, player);
      const rand = seeded(`${ctx.seed}:decktop`);
      const items = cards.map((c, i) => {
        const cg = add(root, new THREE.Group(), 20 + i * 0.1);
        const back = card(ctx, { faceUp: false, player, w: L.cw, brightness: 1 });
        putIn(cg, back.mesh, 20 + i * 0.1);
        const face = card(ctx, { code: c.code ?? null, player, faceUp: c.code != null, w: L.cw, brightness: 1.05 });
        putIn(cg, face.mesh, 20.01 + i * 0.1);
        // 収まった瞬間の硬い白の縁（4 辺。1 フレーム）
        const rim = [0, 1, 2, 3].map(() => putIn(cg, fill(ctx, '#ffffff', 1, 1), 20.02 + i * 0.1));
        const line = add(root, fill(ctx, '#f4f1e6', 1, 1), 19.9 + i * 0.1);
        return {
          cg, back: back.mesh, face: face.mesh, rim, line,
          arc: 120 + rand() * 80, spin: (rand() < 0.5 ? -1 : 1) * (0.5 + rand() * 0.4),
        };
      });

      return {
        root,
        update: ({ localMs: t }) => {
          const sq = squashOf(n);
          const on = t >= 0 && t < sq + SQUASH;
          // --- パネル: 加速して滑り込み → 急停止（反動）→ 見得 → 縦に細く潰れる
          const inK = tween(t, 0, IN_END, 'power2_in');
          const recoil = t >= IN_END ? Math.sin(seg(t, IN_END, IN_END + 90) * Math.PI) * (1 - seg(t, IN_END, IN_END + 90)) : 0;
          const px = cx + side * offDist * (1 - inK) - side * 14 * recoil;
          const sy = 1 - tween(t, sq, sq + SQUASH, 'power3_in');
          const sx = 1 + 0.04 * seg(t, sq, sq + SQUASH);
          place(g, px, cy, { sx, sy: Math.max(0.001, sy) });
          fade(g, on ? 1 : 0);

          // --- 硬い線: 滑り込みの間だけ、来た側の縁から後ろへ
          const len = t < IN_END ? W * 0.35 * seg(t, 0, 40) : W * 0.35 * (1 - seg(t, IN_END, IN_END + 80));
          const tail = px + side * L.w / 2;
          trails.forEach(({ m, k }) => {
            place(m, tail + side * len / 2, cy + k * L.h, { sx: Math.max(0.001, len), sy: k === 0.05 ? 6 : 3 });
            fade(m, on && len > 1 ? 0.9 : 0);
          });

          // --- カード
          items.forEach((it, i) => {
            const d = departOf(i); const l = landOf(i);
            if (t < d || !on) {
              fade(it.cg, 0); fade(it.line, 0);
              return;
            }
            const tx = px + L.slotX(i) * sx;
            const ty = cy;
            const posAt = (u) => [
              from.x + (tx - from.x) * u,
              from.y + (ty - from.y) * u - it.arc * Math.sin(Math.PI * u),
            ];
            const u = flightAt(t, i);
            const [x, y] = posAt(u);
            const s = (from.w + (L.cw - from.w) * u) / L.cw;
            // 収まる前に横に潰れ（裏）→ 収まった瞬間から開く（表）
            const closing = seg(t, l - FLIP, l);
            const opening = seg(t, l, l + FLIP);
            const showFace = t >= l;
            const flipX = showFace ? tween(t, l, l + FLIP, 'expo_out') : 1 - closing;
            place(it.cg, x, y, { sx: s * Math.max(0.02, flipX), sy: s * sy, rot: it.spin * (1 - u) });
            fade(it.cg, 1);
            fade(it.back, showFace ? 0 : 1);
            fade(it.face, showFace ? 1 : 0);
            // 硬い白の縁: 開ききった直後の 1 フレーム
            const flash = t >= l + FLIP * 0.5 && t < l + FLIP * 0.5 + 34 && opening > 0;
            const hw = L.cw / 2 + 3; const hh = L.ch / 2 + 3;
            it.rim.forEach((m, e) => {
              const horiz = e % 2 === 0;
              place(m, horiz ? 0 : (e === 1 ? hw : -hw), horiz ? (e === 0 ? -hh : hh) : 0, { sx: horiz ? hw * 2 + 6 : 6, sy: horiz ? 6 : hh * 2 + 6 });
              fade(m, flash ? 1 : 0);
            });
            // 飛ぶ間の細い硬い線（尾は少し前の位置）
            const [ax, ay] = posAt(flightAt(t - TRAIL_MS, i));
            const ll = Math.hypot(x - ax, y - ay);
            const thin = 1 - seg(t, l - 80, l);
            if (t < l && ll > 1 && thin > 0.02) {
              place(it.line, (x + ax) / 2, (y + ay) / 2, { sx: ll, sy: 3 * thin, rot: Math.atan2(y - ay, x - ax) });
              fade(it.line, 1);
            } else {
              fade(it.line, 0);
            }
          });
        },
      };
    },
  }],
}, 'confirm_decktop_effect.js');
